import {
  ChangeDetectorRef,
  Directive,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  inject,
} from "@angular/core";
import { Observable, Subscription } from "rxjs";
import swal from "sweetalert2";

import { MatrizRiesgoService } from "../matriz-riesgo.service";
import { PantallaCompleta } from "../pantalla-completa";
import {
  Comparacion,
  ComparacionGrupo,
  ComparacionRenglon,
  MatrizEvidencia,
  MatrizEvidenciaRes,
  MatrizRespaldo,
  OrigenEvidencia,
} from "src/app/shared/model/iso42001/matriz-riesgo.model";
import {
  abrirEvidencia,
  bajarEvidencia,
  detalleErrorSubida,
  iconoDeEvidencia,
  tamanoDeArchivo,
} from "./hoja-evidencias";

/** Lo que cambia entre la hoja de Activos y la del Plan Gestión RSK. */
export interface HojaMatriz {
  /** Con qué nombre guarda el servidor sus copias de seguridad. */
  entidad: "ACTIVO" | "RIESGO";
  /** De dónde cuelgan sus evidencias adjuntas. */
  origenEvidencia: OrigenEvidencia;
  /** "este activo" / "este riesgo", para cuando el registro no trae folio. */
  esteRegistro: string;
  /** El aviso de restaurar de la papelera, hasta "tal como estaba ". */
  vuelveDePapelera: string;
  /** El «Listo» de la papelera cuando el servidor no manda detalle. */
  volvioDePapelera: string;
  /** El error al restaurar de la papelera. */
  errorPapelera: string;
  /** "El activo volverá" / "El riesgo volverá", al regresar a una copia. */
  volveraACopia: string;
  /** Lo que además se reemplaza con la copia (vacío si nada más). */
  reemplazaConCopia: string;
  /** El «Listo» al regresar a una copia cuando el servidor no manda detalle. */
  volvioACopia: string;
}

/** Lo que la base lee de un movimiento de la bitácora. */
export interface MovimientoHoja {
  idRespaldo?: number;
  fechaCambio?: Date | string;
}

/**
 * Lo común de las dos hojas con tarjetas del libro FOS-005: la de Activos
 * (MatrizCompletaIA) y la del Plan Gestión RSK (RskCompletoIA).
 *
 * Las dos se cuelgan de <body> para la vista previa, la captura grande y la
 * comparación, y las dos tienen papelera, copias de seguridad, comparación
 * de versiones contra hoy, bitácora con «Regresar a antes» y evidencias por
 * tarjeta. Cada hoja dice cómo se llama su registro, de dónde sale su id, si
 * tiene un formulario a pantalla completa abierto y qué relee después de
 * regresar a una copia.
 *
 * R es el registro de la tarjeta (activo o riesgo) y H el movimiento de su
 * bitácora.
 */
@Directive()
export abstract class HojaMatrizBase<R, H extends MovimientoHoja> implements OnInit, OnChanges, OnDestroy {

  /* ---------- Gestión de Riesgos ----------
   * De qué matriz son los registros de la hoja. Sin dato cae en la 1 (IA),
   * que es como se comportó mientras existió una sola. */
  @Input() idMatriz = 1;

  /** Vista previa del formato FOS-005 a pantalla completa, solo lectura. */
  vistaPrevia = false;

  protected readonly service = inject(MatrizRiesgoService);
  protected readonly cdr = inject(ChangeDetectorRef);

  /** Maneja el sacar y regresar el componente de <body>. */
  protected readonly fuera = new PantallaCompleta(inject<ElementRef<HTMLElement>>(ElementRef).nativeElement);

  /** Nombres, entidad y origen de evidencias de la hoja. */
  protected abstract readonly hoja: HojaMatriz;

  /**
   * La papelera. Un registro eliminado ya no tiene tarjeta, así que su copia
   * sólo se puede alcanzar desde aquí: sin esto, el respaldo del borrado no
   * serviría de nada.
   */
  papeleraAbierta = false;
  papelera: MatrizRespaldo[] = [];
  cargandoPapelera = false;
  restaurandoPapelera = new Set<number>();

  /**
   * Copias de seguridad del registro: completo, con sus cortes, tal como
   * estaba antes de cada cambio y antes de borrarlo. Es lo que permite
   * regresar sin depender del binlog de la base. Se piden al abrir la
   * sección y se releen después de restaurar, nunca antes.
   */
  respaldosAbiertos = new Set<number>();
  respaldos: { [id: number]: MatrizRespaldo[] } = {};
  cargandoRespaldos = new Set<number>();
  restaurando = new Set<number>();

  /**
   * Bitácora por registro: quién cambió qué y cuándo. Se pide sólo al
   * desplegarla, para no traerla de entrada por cada tarjeta.
   */
  bitacoraAbierta = new Set<number>();
  bitacora: { [id: number]: H[] } = {};
  cargandoBitacora = new Set<number>();

  /* ---------- Comparar versiones ---------- */

  /** El registro cuya comparación está abierta, o null si está cerrada. */
  protected comparaRegistro: R | null = null;
  /** La copia que se está enfrentando contra la versión de hoy. */
  comparaIdRespaldo: number | null = null;
  comparacion: Comparacion | null = null;
  comparando = false;
  /** Por omisión sólo lo que cambió: es a lo que se entra a ver. */
  comparaSoloCambios = true;
  /**
   * La vista ya filtrada. Se arma al recibir la comparación y al mover el
   * interruptor, no desde la plantilla: estas pantallas no usan OnPush y un
   * filtro en el HTML se recalcularía en cada ciclo.
   */
  comparaVista: { grupo: ComparacionGrupo; renglones: ComparacionRenglon[] }[] = [];

  /** La comparación en vuelo, para poder cortarla al cerrar o al salir. */
  private subComparacion: Subscription | null = null;

  /* ---------- Evidencias adjuntas ----------
   * Imagen, PDF, Excel... del registro y su tratamiento. Se piden solo al
   * desplegar la sección, igual que la bitácora. */

  evidenciasAbiertas = new Set<number>();
  evidencias: { [id: number]: MatrizEvidencia[] } = {};
  cargandoEvidencias = new Set<number>();
  subiendoEvidencia = new Set<number>();
  /** "SI", "NO" o "" (= aplica); undefined = todavía no se consulta. */
  evidenciaAplica: { [id: number]: string } = {};

  /** Lee de nuevo la hoja completa. */
  abstract cargar(): void;

  /** El id del registro: idMatrizActivo o idRiesgoProceso. */
  protected abstract idDe(registro: R): number | undefined;

  /** Si hay una captura a pantalla completa abierta (el editor o el alta). */
  protected abstract formularioAbierto(): boolean;

  /** Lo que se relee después de regresar el registro a una de sus copias. */
  protected abstract trasRestaurarCopia(id: number): void;

  ngOnInit(): void {
    this.cargar();
  }

  /**
   * El libro REUSA este componente al pasar de una matriz a otra por el menu:
   * Angular solo cambia el @Input, no vuelve a construirlo ni dispara
   * ngOnInit. Sin esto la pantalla se quedaba mostrando los datos de la
   * matriz ANTERIOR, y editar o borrar ahi escribia sobre el area
   * equivocada sin que nada lo avisara: el backend lo permite, porque el
   * permiso se mide contra la matriz del renglon, no contra la que se esta
   * viendo.
   *
   * Solo se recarga cuando el id cambia de verdad, no en el primer cambio
   * (de eso ya se encarga ngOnInit).
   */
  ngOnChanges(cambios: SimpleChanges): void {
    const c = cambios["idMatriz"];
    if (c && !c.firstChange && c.previousValue !== c.currentValue) {
      this.cargar();
    }
  }

  ngOnDestroy(): void {
    this.cortaComparacion();
    this.fuera.liberar();
  }

  /* ---------- Pantalla completa ---------- */

  alternarVista(): void {
    this.vistaPrevia = !this.vistaPrevia;
    this.sincronizarPantalla();
  }

  /**
   * La vista previa, la captura grande y la comparación se dibujan colgadas
   * de <body>: la pestaña de Material abre un contexto de apilamiento y,
   * dentro de ella, la barra superior se dibuja ENCIMA del modal por mucho
   * z-index que se le ponga.
   */
  protected sincronizarPantalla(): void {
    this.fuera.sincronizar(this.vistaPrevia || this.formularioAbierto() || this.comparaRegistro !== null);
    this.cdr.detectChanges();
  }

  /* ---------- Papelera ---------- */

  alternarPapelera(): void {
    this.papeleraAbierta = !this.papeleraAbierta;
    if (this.papeleraAbierta) {
      this.cargaPapelera();
    }
  }

  protected cargaPapelera(): void {
    this.cargandoPapelera = true;
    this.service.respaldosEliminados(this.hoja.entidad, this.idMatriz).subscribe({
      next: (res) => {
        this.cargandoPapelera = false;
        this.papelera = res?.respaldos || [];
        this.cdr.detectChanges();
      },
      error: () => {
        this.cargandoPapelera = false;
        this.papelera = [];
        swal.fire("Error", "No se pudo consultar la papelera.", "error");
        this.cdr.detectChanges();
      },
    });
  }

  /** Resucita el registro con su mismo folio y sus mismos ids. */
  restaurarEliminado(r: MatrizRespaldo): void {
    swal
      .fire({
        title: "¿Restaurar " + (r.referencia || this.hoja.esteRegistro) + "?",
        html:
          this.hoja.vuelveDePapelera +
          "el <b>" + this.fechaLegible(r.fecha) + "</b>.<br><br>" +
          "Como regresa con sus mismos identificadores, sus evidencias vuelven a quedar ligadas solas.",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Sí, restaurar",
        cancelButtonText: "Cancelar",
      })
      .then((resp) => {
        if (!resp.isConfirmed) {
          return;
        }
        const idResp = r.idRespaldo!;
        this.restaurandoPapelera.add(idResp);
        this.cdr.detectChanges();
        this.service.restaurarRespaldo(idResp).subscribe({
          next: (res) => {
            this.restaurandoPapelera.delete(idResp);
            if (!res || res.codigoError) {
              swal.fire("No se restauró", res?.mensajeError || "Error desconocido.", "warning");
              this.cdr.detectChanges();
              return;
            }
            this.cargaPapelera();
            this.cargar();
            swal.fire("Listo", res.detalle || this.hoja.volvioDePapelera, "success");
          },
          error: () => {
            this.restaurandoPapelera.delete(idResp);
            swal.fire("Error", this.hoja.errorPapelera, "error");
            this.cdr.detectChanges();
          },
        });
      });
  }

  /* ---------- Copias de seguridad ---------- */

  alternarRespaldos(registro: R): void {
    const id = this.idDe(registro)!;
    if (this.respaldosAbiertos.has(id)) {
      this.respaldosAbiertos.delete(id);
      return;
    }
    this.respaldosAbiertos.add(id);
    if (this.respaldos[id]) {
      return;
    }
    this.cargaRespaldos(id);
  }

  protected cargaRespaldos(id: number): void {
    this.cargandoRespaldos.add(id);
    this.service.respaldos(this.hoja.entidad, id).subscribe({
      next: (res) => {
        this.cargandoRespaldos.delete(id);
        this.respaldos[id] = res?.respaldos || [];
        this.cdr.detectChanges();
      },
      error: () => {
        this.cargandoRespaldos.delete(id);
        this.respaldos[id] = [];
        swal.fire("Error", "No se pudieron consultar las copias.", "error");
        this.cdr.detectChanges();
      },
    });
  }

  /** Qué se guardó en esa copia, en palabras. */
  motivoRespaldo(r: MatrizRespaldo): string {
    switch (r.motivo) {
      case "BORRADO":
        return "Antes de eliminarlo";
      case "RESTAURACION":
        return "Antes de restaurar";
      default:
        return "Antes de un cambio";
    }
  }

  /**
   * Regresa el registro a esa copia. Se pregunta antes con swal, como el resto
   * de las acciones que no se pueden deshacer con Ctrl+Z — aunque ésta sí se
   * puede: restaurar deja su propia copia del estado que pisó.
   */
  restaurarRespaldo(registro: R, r: MatrizRespaldo): void {
    const id = this.idDe(registro)!;
    swal
      .fire({
        title: "¿Regresar a esta copia?",
        html:
          this.hoja.volveraACopia + " a como estaba el <b>" +
          this.fechaLegible(r.fecha) +
          "</b>.<br><br>" + this.hoja.reemplazaConCopia +
          "Los cortes de seguimiento no se borran: sólo se reponen los que falten.<br><br>" +
          "Antes de tocar nada se guarda otra copia de cómo está ahora, así que esto también se puede deshacer.",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Sí, regresar",
        cancelButtonText: "Cancelar",
      })
      .then((resp) => {
        if (!resp.isConfirmed) {
          return;
        }
        this.restaurando.add(id);
        this.cdr.detectChanges();
        this.service.restaurarRespaldo(r.idRespaldo!).subscribe({
          next: (res) => {
            this.restaurando.delete(id);
            if (!res || res.codigoError) {
              swal.fire("No se restauró", res?.mensajeError || "Error desconocido.", "warning");
              this.cdr.detectChanges();
              return;
            }
            // La copia nueva (la del estado que se acaba de pisar) y el
            // registro cambiado: las listas se releen. Sin esto la sección se
            // quedaba con la lista vieja hasta recargar la página.
            delete this.respaldos[id];
            delete this.bitacora[id];
            this.cargaRespaldos(id);
            this.trasRestaurarCopia(id);
            swal.fire("Listo", res.detalle || this.hoja.volvioACopia, "success");
          },
          error: () => {
            this.restaurando.delete(id);
            swal.fire("Error", "No se pudo restaurar la copia.", "error");
            this.cdr.detectChanges();
          },
        });
      });
  }

  /* ---------- Comparar versiones ---------- */

  /**
   * Enfrenta esa copia contra el registro de hoy. La comparación la calcula
   * el servidor: ahí están los nombres de campo en español y los catálogos
   * para traducir los niveles. Aquí sólo se pinta.
   */
  abrirComparacion(registro: R, c: MatrizRespaldo): void {
    if (!c.idRespaldo) {
      return;
    }
    this.comparaRegistro = registro;
    this.comparaSoloCambios = true;
    this.sincronizarPantalla();
    this.cargaComparacion(c.idRespaldo);
  }

  private cargaComparacion(idRespaldo: number): void {
    this.comparaIdRespaldo = idRespaldo;
    this.comparacion = null;
    this.comparaVista = [];
    this.comparando = true;
    this.cdr.detectChanges();
    // Se guarda para poder cortarla: es la petición más lenta de la pantalla
    // y la ventana se puede cerrar —o se puede salir del módulo— antes de que
    // conteste.
    this.cortaComparacion();
    this.subComparacion = this.service.compararRespaldo(idRespaldo).subscribe({
      next: (res) => {
        // Si mientras contestaba se pidió otra versión —o se cerró la
        // ventana— esta respuesta ya no es la que se está viendo. Sin esta
        // guarda, la más lenta pintaba encima de la más nueva.
        if (this.comparaIdRespaldo !== idRespaldo) {
          return;
        }
        this.comparando = false;
        if (!res || res.codigoError || !res.comparacion) {
          this.cerrarComparacion();
          swal.fire(
            "No se pudo comparar",
            res?.mensajeError || "Error desconocido.",
            "warning"
          );
          this.cdr.detectChanges();
          return;
        }
        this.comparacion = res.comparacion;
        this.armaVistaComparacion();
        this.cdr.detectChanges();
      },
      error: () => {
        if (this.comparaIdRespaldo !== idRespaldo) {
          return;
        }
        this.comparando = false;
        this.cerrarComparacion();
        swal.fire("Error", "No se pudo comparar con esa versión.", "error");
        this.cdr.detectChanges();
      },
    });
  }

  /** Cambiar de versión sin cerrar la ventana. */
  comparaOtraVersion(id: number | null): void {
    if (!id || id === this.comparaIdRespaldo) {
      return;
    }
    this.cargaComparacion(id);
  }

  alternarSoloCambios(): void {
    this.comparaSoloCambios = !this.comparaSoloCambios;
    this.armaVistaComparacion();
  }

  cerrarComparacion(): void {
    this.cortaComparacion();
    this.comparaRegistro = null;
    this.comparaIdRespaldo = null;
    this.comparacion = null;
    this.comparaVista = [];
    this.comparando = false;
    // Devuelve la pantalla a su sitio dentro de la pestaña.
    this.fuera.sincronizar(this.vistaPrevia || this.formularioAbierto());
  }

  /** Las copias del registro abierto, para el selector de versión. */
  get comparaVersiones(): MatrizRespaldo[] {
    const id = this.comparaRegistro ? this.idDe(this.comparaRegistro) : null;
    return (id && this.respaldos[id]) || [];
  }

  private armaVistaComparacion(): void {
    const grupos = this.comparacion?.grupos || [];
    this.comparaVista = [];
    grupos.forEach((g) => {
      if (this.comparaSoloCambios && g.estado === "IGUAL") {
        return;
      }
      // Un bloque que nació o que se fue se enseña entero: su contenido ES la
      // noticia. Filtrarle renglones lo dejaría a medias.
      const completo =
        !this.comparaSoloCambios || g.estado === "AGREGADO" || g.estado === "ELIMINADO";
      const renglones = (g.renglones || []).filter(
        (r) => completo || (r.estado !== "IGUAL" && r.estado !== "SIN_DATO")
      );
      this.comparaVista.push({ grupo: g, renglones });
    });
  }

  private cortaComparacion(): void {
    if (this.subComparacion) {
      this.subComparacion.unsubscribe();
      this.subComparacion = null;
    }
  }

  /** Cuántos bloques se están ocultando por no tener cambios. */
  get comparaOcultos(): number {
    const total = this.comparacion?.grupos?.length || 0;
    return total - this.comparaVista.length;
  }

  claseComparacion(estado?: string): string {
    switch (estado) {
      case "AGREGADO":
        return "cmp-alta";
      case "ELIMINADO":
        return "cmp-baja";
      case "CAMBIADO":
        return "cmp-cambio";
      case "SIN_DATO":
        return "cmp-sin";
      default:
        return "cmp-igual";
    }
  }

  /**
   * La clase del RENGLÓN. Aparte de la de la pastilla a propósito: aquélla
   * lleva fondo y borde de color, que sobre un renglón entero resultan
   * ilegibles en los temas oscuro y cristal. Aquí basta un filo de color.
   */
  claseRenglonComparacion(estado?: string): string {
    switch (estado) {
      case "AGREGADO":
        return "dif-alta";
      case "ELIMINADO":
        return "dif-baja";
      case "CAMBIADO":
        return "dif-cambio";
      case "SIN_DATO":
        return "dif-sin";
      default:
        return "dif-igual";
    }
  }

  etiquetaComparacion(estado?: string): string {
    switch (estado) {
      case "AGREGADO":
        return "Nuevo";
      case "ELIMINADO":
        return "Se quitó";
      case "CAMBIADO":
        return "Cambió";
      case "SIN_DATO":
        return "No se guardó";
      default:
        return "Igual";
    }
  }

  /* ---------- Bitácora ---------- */

  /**
   * Regresa el registro a como estaba justo antes de ESE movimiento. La copia
   * la señala el propio renglón (idRespaldo), así que no hay forma de
   * equivocarse de versión.
   */
  restaurarDesdeBitacora(registro: R, h: H): void {
    if (!h.idRespaldo) {
      return;
    }
    this.restaurarRespaldo(registro, { idRespaldo: h.idRespaldo, fecha: h.fechaCambio });
  }

  /** dd/MM/yyyy HH:mm, para el texto del diálogo. */
  fechaLegible(f?: Date | string): string {
    if (!f) {
      return "—";
    }
    const d = new Date(f);
    if (Number.isNaN(d.getTime())) {
      return String(f);
    }
    const dos = (n: number) => String(n).padStart(2, "0");
    return (
      dos(d.getDate()) + "/" + dos(d.getMonth() + 1) + "/" + d.getFullYear() +
      " " + dos(d.getHours()) + ":" + dos(d.getMinutes())
    );
  }

  /* ---------- Evidencias adjuntas ---------- */

  alternarEvidencias(registro: R): void {
    const id = this.idDe(registro)!;
    if (this.evidenciasAbiertas.has(id)) {
      this.evidenciasAbiertas.delete(id);
      return;
    }
    this.evidenciasAbiertas.add(id);
    this.cargarEvidencias(id);
  }

  protected cargarEvidencias(id: number): void {
    this.cargandoEvidencias.add(id);
    this.service.consultarEvidencias(this.hoja.origenEvidencia, id).subscribe({
      next: (res) => {
        this.cargandoEvidencias.delete(id);
        this.evidencias[id] = (res && !res.codigoError && res.evidencias) || [];
        this.evidenciaAplica[id] = (res && !res.codigoError && res.aplica) || "";
        this.cdr.detectChanges();
      },
      error: () => {
        this.cargandoEvidencias.delete(id);
        this.evidencias[id] = [];
        this.cdr.detectChanges();
      },
    });
  }

  /**
   * Sigue la subida de una evidencia que la hoja ya validó y mandó: avisa si
   * el servidor la rechazó o no contestó y, si entró, relee la lista.
   */
  protected vigilaSubida(id: number, subida: Observable<MatrizEvidenciaRes>): void {
    subida.subscribe({
      next: (res) => {
        this.subiendoEvidencia.delete(id);
        if (!res || res.codigoError) {
          swal.fire("No se pudo subir",
            res?.mensajeError
              || "El servidor respondió sin decir qué falló. El detalle quedó en el log del servidor: busca 'MatrizEvidenciaService: subir'.",
            "error");
        } else {
          this.cargarEvidencias(id);
        }
        this.cdr.detectChanges();
      },
      error: (e) => {
        this.subiendoEvidencia.delete(id);
        swal.fire("No se pudo subir", detalleErrorSubida(e), "error");
        this.cdr.detectChanges();
      },
    });
  }

  verEvidencia(evd: MatrizEvidencia): void {
    abrirEvidencia(this.service, evd);
  }

  guardarEvidencia(evd: MatrizEvidencia): void {
    bajarEvidencia(this.service, evd);
  }

  protected guardarAplicaEvidencias(id: number, aplica: string): void {
    this.service.marcarAplicaEvidencia(this.hoja.origenEvidencia, id, aplica).subscribe({
      next: (res) => {
        if (!res || res.codigoError) {
          swal.fire("No se pudo guardar", res?.mensajeError || "", "error");
        } else {
          this.evidenciaAplica[id] = aplica;
          if (aplica === "NO") {
            this.evidenciasAbiertas.delete(id);
          }
        }
        this.cdr.detectChanges();
      },
      error: () => swal.fire("No se pudo guardar", "Error al guardar la bandera.", "error"),
    });
  }

  iconoEvidencia(evd: MatrizEvidencia): string {
    return iconoDeEvidencia(evd);
  }

  tamanoLegible(bytes?: number): string {
    return tamanoDeArchivo(bytes);
  }

  /* ---------- Fechas de los cortes ---------- */

  /** La fecha en el formato que espera el <input type="date">. */
  protected aFecha(f?: Date | string): string {
    if (!f) {
      return "";
    }
    const d = new Date(f as string);
    return d.getFullYear() + "-" +
           ("0" + (d.getMonth() + 1)).slice(-2) + "-" +
           ("0" + d.getDate()).slice(-2);
  }

  /** dd/MM/yyyy, sin la hora. */
  protected soloFecha(f?: Date | string): string {
    if (!f) {
      return "";
    }
    const d = new Date(f as string);
    return ("0" + d.getDate()).slice(-2) + "/" +
           ("0" + (d.getMonth() + 1)).slice(-2) + "/" + d.getFullYear();
  }
}
